import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from "exceljs"; 
import { saveAs } from "file-saver"; 
import ManagerSidebar from '../components/Sidebar/ManagerSidebar';
import './IncomeReport.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDownload, faCalendarAlt, faWallet,
    faArrowTrendUp, faBuilding
} from '@fortawesome/free-solid-svg-icons';

const IncomeReport = () => {
    const [tenants, setTenants] = useState([]);
    const [filteredReportData, setFilteredReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [reportType, setReportType] = useState('summary');
    const isMounted = useRef(true);

    // Helper function to calculate months between arrival and departure
    const getCalculatedMonths = (arrival, departure) => {
        const start = new Date(arrival);
        const end = new Date(departure);
        
        let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
        
        // If it starts and ends within the same calendar month, count as 1
        return months <= 0 ? 1 : months;
    };

    const fetchAllTenants = async () => {
        setLoading(true);
        try {
            const response = await axios.get('https://ikaze-thouse-backend.vercel.app/api/v1/tenants/getAll');
            if (response.data.success && isMounted.current) {
                setTenants(response.data.data);
                
                // Fallback initial view
                setFilteredReportData({ 
                    type: 'summary', 
                    tenants: response.data.data.map(t => ({ ...t, rent_rwf: parseFloat(t.rent_rwf) || 0 })) 
                });
            }
        } catch (err) {
            console.error('Error fetching tenants data.');
        } finally {
            if (isMounted.current) setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllTenants();
        return () => { isMounted.current = false; };
    }, []);

    const formatRWF = (value) => {
        const num = parseFloat(value) || 0;
        return num.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }) + ' RWF';
    };

    const calculateTotal = (arr) =>
        arr.reduce((sum, t) => sum + (parseFloat(t.rent_rwf) || 0), 0);

    const getGrandTotal = () => {
        if (!filteredReportData) return 0;
        if (filteredReportData.type === 'summary') return calculateTotal(filteredReportData.tenants);
        const sections = filteredReportData.units || filteredReportData.data || {};
        return Object.values(sections).reduce((acc, curr) => acc + calculateTotal(curr), 0);
    };

    const generateReport = useCallback(() => {
        const startFilter = startDate ? new Date(startDate) : null;
        if (startFilter) startFilter.setHours(0, 0, 0, 0);

        const endFilter = endDate ? new Date(endDate) : null;
        if (endFilter) endFilter.setHours(23, 59, 59, 999);

        const processedAndFiltered = [];

        tenants.forEach(t => {
            const arrDate = new Date(t.arrival_date);
            const depDate = new Date(t.departure_date);
            const totalRent = parseFloat(t.rent_rwf) || 0;
            
            const totalMonths = getCalculatedMonths(t.arrival_date, t.departure_date);
            const monthlyRent = totalRent / totalMonths;

            // Long term stays spanning across multiple months
            if (totalMonths >= 2) {
                let currentIteration = new Date(arrDate.getFullYear(), arrDate.getMonth(), 1);
                
                while (currentIteration <= depDate) {
                    const isDepartureMonth = currentIteration.getMonth() === depDate.getMonth() && 
                                             currentIteration.getFullYear() === depDate.getFullYear();

                    // 🛑 NEW STRICER CONDITION: Do not allocate money to the departure month if checkin wasn't also in this month
                    if (isDepartureMonth && (arrDate.getMonth() !== depDate.getMonth() || arrDate.getFullYear() !== depDate.getFullYear())) {
                        currentIteration.setMonth(currentIteration.getMonth() + 1);
                        continue; 
                    }

                    const nextMonthBoundary = new Date(currentIteration.getFullYear(), currentIteration.getMonth() + 1, 0, 23, 59, 59, 999);
                    
                    const isMonthInFilter = 
                        (!startFilter || nextMonthBoundary >= startFilter) && 
                        (!endFilter || currentIteration <= endFilter);

                    if (isMonthInFilter) {
                        const trackingDate = new Date(currentIteration.getFullYear(), currentIteration.getMonth(), Math.min(arrDate.getDate(), 28));
                        
                        processedAndFiltered.push({
                            ...t,
                            arrival_date: trackingDate.toISOString(), 
                            rent_rwf: monthlyRent 
                        });
                    }
                    currentIteration.setMonth(currentIteration.getMonth() + 1);
                }
            } 
            // Short term bookings inside the same single month block
            else {
                if ((!startFilter || arrDate >= startFilter) && (!endFilter || arrDate <= endFilter)) {
                    processedAndFiltered.push({
                        ...t,
                        rent_rwf: totalRent 
                    });
                }
            }
        });

        // Structure view allocations dynamically 
        if (reportType === 'summary') {
            setFilteredReportData({ type: 'summary', tenants: processedAndFiltered });
        } else if (reportType === 'per-unit') {
            const units = processedAndFiltered.reduce((acc, t) => {
                const name = t.appartment_unite || 'Unassigned';
                if (!acc[name]) acc[name] = [];
                acc[name].push(t);
                return acc;
            }, {});
            setFilteredReportData({ type: 'per-unit', units });
        } else {
            const history = processedAndFiltered.reduce((acc, t) => {
                const date = new Date(t.arrival_date).toLocaleString('default', { month: 'long', year: 'numeric' });
                if (!acc[date]) acc[date] = [];
                acc[date].push(t);
                return acc;
            }, {});
            setFilteredReportData({ type: 'historical', data: history });
        }
    }, [tenants, startDate, endDate, reportType]);

    const handleDownloadPdf = () => {
        const doc = new jsPDF();
        const blueBrand = [37, 99, 235];

        doc.setFillColor(...blueBrand);
        doc.rect(0, 0, 210, 40, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(22);
        doc.setFont("helvetica", "bold");
        doc.text("GKM INVEST", 14, 22);

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text("FINANCIAL INCOME STATEMENT", 14, 32);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 140, 32);

        doc.setTextColor(40, 40, 40);
        doc.setFontSize(14);
        doc.text(`${reportType.replace('-', ' ').toUpperCase()} REPORT`, 14, 55);

        let currentY = 60;
        const sections = filteredReportData?.units || filteredReportData?.data || {};

        if (filteredReportData?.type === 'summary') {
            autoTable(doc, {
                startY: currentY,
                head: [['Guest Name', 'Unit', 'Arrival', 'Departure', 'Allocated Amount']],
                body: filteredReportData.tenants.map(t => [
                    t.guest_name,
                    t.appartment_unite,
                    new Date(t.arrival_date).toLocaleDateString(),
                    new Date(t.departure_date).toLocaleDateString(),
                    formatRWF(t.rent_rwf)
                ]),
                foot: [['', '', '', 'GRAND TOTAL', formatRWF(getGrandTotal())]],
                headStyles: { fillColor: blueBrand },
                theme: 'striped'
            });
        } else {
            Object.entries(sections).forEach(([key, list]) => {
                doc.setFontSize(11);
                doc.setFont("helvetica", "bold");
                doc.text(`${key}`, 14, currentY + 10);

                autoTable(doc, {
                    startY: currentY + 15,
                    head: [['Guest Name', 'Timeline Context', 'Allocated Rent']],
                    body: list.map(t => [
                        t.guest_name,
                        `${new Date(t.arrival_date).toLocaleString('default', {month:'short', year:'numeric'})}`,
                        formatRWF(t.rent_rwf)
                    ]),
                    theme: 'grid'
                });

                currentY = doc.lastAutoTable.finalY + 10;

                doc.setFontSize(11);
                doc.setFont("helvetica", "bold");
                doc.text(`Subtotal: ${formatRWF(calculateTotal(list))}`, 14, currentY);
                currentY += 10;
            });

            doc.setFontSize(14);
            doc.text(`TOTAL REVENUE: ${formatRWF(getGrandTotal())}`, 14, currentY + 10);
        }

        doc.save(`GKM_Income_${Date.now()}.pdf`);
    };

    const handleDownloadExcel = async () => {
        if (!filteredReportData) return;

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("Income Statements");

        sheet.columns = [
            { id: 'A', width: 26 },
            { id: 'B', width: 18 },
            { id: 'C', width: 16 },
            { id: 'D', width: 16 },
            { id: 'E', width: 22 }
        ];

        sheet.mergeCells('A1:E1');
        const titleCell = sheet.getCell('A1');
        titleCell.value = 'GKM INVEST COMPANY LTD';
        titleCell.font = { size: 18, bold: true, color: { argb: 'FFFFFF' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '1E40AF' } };
        sheet.getRow(1).height = 38;

        sheet.mergeCells('A2:E2');
        const subtitleCell = sheet.getCell('A2');
        subtitleCell.value = `INCOME STATEMENT (${reportType.replace('-', ' ').toUpperCase()} VIEW)`;
        subtitleCell.font = { size: 13, bold: true, color: { argb: '1F2937' } };
        subtitleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getRow(2).height = 26;

        sheet.mergeCells('A3:E3');
        const dateCell = sheet.getCell('A3');
        dateCell.value = `Generated on: ${new Date().toLocaleString()}`;
        dateCell.font = { size: 10, italic: true, color: { argb: '4B5563' } };
        dateCell.alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.getRow(3).height = 20;

        let currentLine = 5; 

        const styleHeaders = (rowObj, headerValues) => {
            rowObj.values = headerValues;
            rowObj.height = 24;
            rowObj.eachCell(cell => {
                cell.font = { bold: true, color: { argb: 'FFFFFF' }, size: 11 };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '2563EB' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            });
        };

        if (filteredReportData.type === 'summary') {
            const hRow = sheet.getRow(currentLine);
            styleHeaders(hRow, ['Guest Name', 'Unit Apartment', 'Target Month', '', 'Allocated Amount (RWF)']);
            sheet.mergeCells(`C${currentLine}:D${currentLine}`);
            currentLine++;

            filteredReportData.tenants.forEach((t, idx) => {
                const rent = parseFloat(t.rent_rwf || 0);
                const row = sheet.addRow([
                    t.guest_name,
                    t.appartment_unite,
                    new Date(t.arrival_date).toLocaleString('default', {month: 'long', year: 'numeric'}),
                    '',
                    rent
                ]);
                row.height = 20;
                row.alignment = { vertical: 'middle' };
                row.getCell(5).numFmt = '"RWF" #,##0.00';
                sheet.mergeCells(`C${currentLine}:D${currentLine}`);
                if (idx % 2 === 0) {
                    row.eachCell(c => c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F8FAFC' } });
                }
                currentLine++;
            });

            const fRow = sheet.addRow(['', 'GRAND TOTAL', '', '', getGrandTotal()]);
            fRow.height = 24;
            fRow.font = { bold: true };
            fRow.eachCell(c => {
                c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DBEAFE' } };
                c.alignment = { vertical: 'middle' };
            });
            fRow.getCell(5).numFmt = '"RWF" #,##0.00';

        } else {
            const sections = filteredReportData.units || filteredReportData.data || {};

            Object.entries(sections).forEach(([groupName, transactions]) => {
                sheet.mergeCells(`A${currentLine}:E${currentLine}`);
                const gCell = sheet.getCell(`A${currentLine}`);
                gCell.value = `CATEGORY / GROUP: ${groupName.toUpperCase()}`;
                gCell.font = { bold: true, size: 11, color: { argb: '1E3A8A' } };
                gCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'EFF6FF' } };
                sheet.getRow(currentLine).height = 24;
                currentLine++;

                const hRow = sheet.getRow(currentLine);
                styleHeaders(hRow, ['Guest Name', 'Operational Period', '', '', 'Allocated Amount']);
                sheet.mergeCells(`B${currentLine}:D${currentLine}`);
                currentLine++;

                transactions.forEach((t, idx) => {
                    const rentVal = parseFloat(t.rent_rwf || 0);
                    const row = sheet.addRow([
                        t.guest_name,
                        new Date(t.arrival_date).toLocaleString('default', {month: 'long', year: 'numeric'}),
                        '',
                        '',
                        rentVal
                    ]);
                    row.height = 20;
                    row.alignment = { vertical: 'middle' };
                    row.getCell(5).numFmt = '"RWF" #,##0.00';
                    sheet.mergeCells(`B${currentLine}:D${currentLine}`);
                    if (idx % 2 === 0) {
                        row.eachCell(c => c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FAFAFA' } });
                    }
                    currentLine++;
                });

                const subRow = sheet.addRow(['', `Subtotal (${groupName})`, '', '', calculateTotal(transactions)]);
                subRow.height = 22;
                subRow.font = { bold: true, italic: true };
                subRow.eachCell(c => c.alignment = { vertical: 'middle' });
                subRow.getCell(5).numFmt = '"RWF" #,##0.00';
                currentLine += 2; 
            });

            const grandRow = sheet.addRow(['', 'MASTER GRAND TOTAL REVENUE', '', '', getGrandTotal()]);
            grandRow.height = 26;
            grandRow.font = { bold: true, size: 12 };
            grandRow.eachCell(c => {
                c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DCFCE7' } };
                c.alignment = { vertical: 'middle' };
            });
            grandRow.getCell(5).numFmt = '"RWF" #,##0.00';
        }

        sheet.eachRow((row, rowNum) => {
            if (rowNum < 4) return;
            row.eachCell(cell => {
                cell.border = {
                    top: { style: 'thin', color: { argb: 'E2E8F0' } },
                    left: { style: 'thin', color: { argb: 'E2E8F0' } },
                    bottom: { style: 'thin', color: { argb: 'E2E8F0' } },
                    right: { style: 'thin', color: { argb: 'E2E8F0' } }
                };
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, `GKM_Income_${Date.now()}.xlsx`);
    };

    return (
        <div className="income-report-wrapper">
            <ManagerSidebar />
            <main className="income-report-main">

                <header className="income-header">
                    <div className="title-area">
                        <h1>Financial Analytics</h1>
                        <p>Real-time revenue tracking & property performance</p>
                    </div>

                    <div className="header-actions" style={{ display: 'flex', gap: '10px' }}>
                        <button className="export-pdf-btn" onClick={handleDownloadPdf}>
                            <FontAwesomeIcon icon={faDownload} /> Export PDF
                        </button>

                        <button className="export-pdf-btn" onClick={handleDownloadExcel}>
                            <FontAwesomeIcon icon={faDownload} /> Export Excel
                        </button>
                    </div>
                </header>

                <div className="income-stats-grid">
                    <div className="stat-card blue">
                        <div className="stat-icon"><FontAwesomeIcon icon={faWallet} /></div>
                        <div className="stat-info">
                            <label>Total Revenue</label>
                            <h3>{formatRWF(getGrandTotal())}</h3>
                        </div>
                    </div>

                    <div className="stat-card green">
                        <div className="stat-icon"><FontAwesomeIcon icon={faArrowTrendUp} /></div>
                        <div className="stat-info">
                            <label>Occupancy Rate</label>
                            <h3>{tenants.length > 0 ? '94%' : '0%'}</h3>
                        </div>
                    </div>

                    <div className="stat-card slate">
                        <div className="stat-icon"><FontAwesomeIcon icon={faBuilding} /></div>
                        <div className="stat-info">
                            <label>Active Units</label>
                            <h3>{reportType === 'per-unit'
                                ? Object.keys(filteredReportData?.units || {}).length
                                : '--'}
                            </h3>
                        </div>
                    </div>
                </div>

                <div className="income-filter-bar">
                    <div className="filter-item">
                        <label><FontAwesomeIcon icon={faCalendarAlt} /> From</label>
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                    </div>

                    <div className="filter-item">
                        <label><FontAwesomeIcon icon={faCalendarAlt} /> To</label>
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                    </div>

                    <div className="filter-item">
                        <label>Report Type</label>
                        <select value={reportType} onChange={e => setReportType(e.target.value)}>
                            <option value="summary">Consolidated Summary</option>
                            <option value="per-unit">Group by Apartment</option>
                            <option value="historical">Monthly Breakdown</option>
                        </select>
                    </div>

                    <button className="apply-btn" onClick={generateReport}>Update View</button>
                </div>

                <div className="income-data-container">
                    {loading ? (
                        <div className="loader">Syncing financial records...</div>
                    ) : (
                        <>
                            {filteredReportData?.type === 'summary' ? (
                                <div className="data-table-wrapper card-shadow">
                                    <table className="modern-table">
                                        <thead>
                                            <tr>
                                                <th>Guest</th>
                                                <th>Unit</th>
                                                <th>Financial Month Context</th>
                                                <th>Allocated Amount</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {filteredReportData.tenants.map((t, i) => (
                                                <tr key={i}>
                                                    <td><strong>{t.guest_name}</strong></td>
                                                    <td>{t.appartment_unite}</td>
                                                    <td>{new Date(t.arrival_date).toLocaleString('default', {month:'long', year:'numeric'})}</td>
                                                    <td>{formatRWF(t.rent_rwf)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="grouped-sections">
                                    {Object.entries(filteredReportData?.units || filteredReportData?.data || {}).map(([key, list], i) => (
                                        <div key={i} className="grouped-card card-shadow">
                                            <div className="group-header">
                                                <h4>{key}</h4>
                                                <span>{list.length} Records</span>
                                            </div>

                                            <table className="modern-table">
                                                <thead>
                                                    <tr>
                                                        <th>Guest Name</th>
                                                        <th>Allocation Period</th>
                                                        <th>Allocated Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {list.map((t, idx) => (
                                                        <tr key={idx}>
                                                            <td>{t.guest_name}</td>
                                                            <td>{new Date(t.arrival_date).toLocaleString('default', {month:'long', year:'numeric'})}</td>
                                                            <td>{formatRWF(t.rent_rwf)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>

                                            <div className="group-footer">
                                                Subtotal: <strong>{formatRWF(calculateTotal(list))}</strong>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

            </main>
        </div>
    );
};

export default IncomeReport;